import type { TenantRead } from "@harmonyso/tenants-sdk";
import { isRemoteCell, type Cell } from "./cells";

export interface CellTenantsResult {
  cell: Cell;
  tenants?: TenantRead[];
  error?: Error | null;
  isLoading?: boolean;
}

export interface CellFailure {
  cell: Cell;
  error: Error;
}

/**
 * Why a tenant returned by `cell` doesn't belong there, or null when its cell
 * and data residency both match. A primary-cell tenants service that predates
 * these fields returns neither, and its tenants are all in the primary cell.
 */
export function describeCellMismatch(
  tenant: Pick<TenantRead, "deployment_cell" | "data_residency">,
  cell: Cell,
): string | null {
  const isPrimary = !isRemoteCell(cell);
  const reportedCell = tenant.deployment_cell ?? (isPrimary ? cell.id : null);
  const reportedResidency =
    tenant.data_residency ?? (isPrimary ? cell.residency : null);
  if (reportedCell === cell.id && reportedResidency === cell.residency) {
    return null;
  }
  return `Expected ${cell.id} (${cell.residency}) but the tenant reports ${
    tenant.deployment_cell ?? "no cell"
  } (${tenant.data_residency ?? "no residency"}).`;
}

/**
 * Merges every cell's tenant list. A cell that failed, or that returned a
 * tenant stamped with another cell or residency, contributes nothing and is
 * reported as a failure, so a wrong base URL can't make one cell's tenants
 * look like another's.
 */
export function mergeCellTenants(results: CellTenantsResult[]): {
  tenants: TenantRead[];
  failures: CellFailure[];
  loadingCells: Cell[];
} {
  const tenants: TenantRead[] = [];
  const failures: CellFailure[] = [];
  const loadingCells: Cell[] = [];

  for (const { cell, tenants: cellTenants, error, isLoading } of results) {
    if (error) {
      failures.push({ cell, error });
      continue;
    }
    if (!cellTenants) {
      if (isLoading) {
        loadingCells.push(cell);
      }
      continue;
    }

    const mismatch = cellTenants
      .map((tenant) => describeCellMismatch(tenant, cell))
      .find((reason) => reason !== null);
    if (mismatch) {
      failures.push({
        cell,
        error: new Error(
          `The ${cell.id} cell returned tenants from another cell. ${mismatch} Check its API base URL.`,
        ),
      });
      continue;
    }

    for (const tenant of cellTenants) {
      tenants.push({
        ...tenant,
        deployment_cell: cell.id,
        data_residency: cell.residency,
      });
    }
  }

  return { tenants, failures, loadingCells };
}
